import saveAs from 'file-saver';
import {
  conflictErrorMsgWithReloadHint,
  conflictPendingVersand,
  ErrorType
} from '../../shared/utils/Error/ErrorCode';
import { useMutation } from '@tanstack/react-query';
import { BehApiRoutes, behClient } from '../beh-api';
import { useNotification } from '../../shared/notification/useNotification';

export function useUpdateCase() {
  const { notifyUserFromRequest, notifyUser } = useNotification();

  const { mutateAsync: updateCase, isPending: isUpdating } = useMutation({
    mutationFn: ({
      updatedData,
      caseId
    }: {
      caseId: string;
      updatedData: { bearbeitungsstand: string; caseVersion: string };
    }) =>
      notifyUserFromRequest(
        () => behClient.patch(BehApiRoutes.case(caseId), updatedData),
        {
          successMessage: 'Der Antrag wurde erfolgreich aktualisiert.',
          errorMessage: 'Der Vermerk konnte nicht erstellt werden.',
          errorIdHandler: {
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_UPDATE_NOT_ALLOWED]: () => {
              notifyUser({
                intent: 'error',
                title: conflictErrorMsgWithReloadHint
              });
            },
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_USER_LOGGED_IN]: () => {
              notifyUser({
                intent: 'error',
                title:
                  'Die Benutzer:in ist aktuell eingeloggt, es konnte keine Zuweisung vorgenommen werden'
              });
            },
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_PENDING_VERSAND]: () => {
              notifyUser({
                intent: 'error',
                title: conflictPendingVersand
              });
            }
          }
        }
      )
  });

  const { mutateAsync: downloadSummary, isPending: isDownloadingSummary } =
    useMutation({
      mutationFn: async ({
        caseId,
        attachmentIds,
        filename
      }: {
        caseId: string;
        filename: string;
        attachmentIds?: string | undefined;
      }) =>
        notifyUserFromRequest(
          () =>
            behClient
              .get<Blob>(
                BehApiRoutes.caseAttachmentSummary(caseId, attachmentIds),
                { responseType: 'blob', headers: { Accept: 'application/zip' } }
              )
              .then((file) => {
                saveAs(file.data as Blob, filename, { autoBom: false });
                return file;
              }),
          {
            successMessage: 'Der Antrag wurde erfolgreich heruntergeladen.',
            loadingMessage: 'Der Antrag wird heruntergeladen',
            errorMessage: 'Der Antrag konnte nicht heruntergeladen werden'
          }
        )
    });

  return {
    updateCase,
    downloadSummary,
    loadingUpdateCase: isUpdating
  };
}
